import * as React from 'react';
import { cn } from '../../lib/utils';

const Switch = React.forwardRef(
  (
    {
      className,
      checked,
      defaultChecked = false,
      onCheckedChange,
      disabled = false,
      ...props
    },
    ref,
  ) => {
    const { onClick, onKeyDown, ...rest } = props;
    const [internalChecked, setInternalChecked] = React.useState(defaultChecked);
    const isControlled = checked !== undefined;
    const isChecked = isControlled ? checked : internalChecked;

    const setChecked = React.useCallback(
      (value, event) => {
        if (!isControlled) {
          setInternalChecked(value);
        }
        if (onCheckedChange) {
          onCheckedChange(value, event);
        }
      },
      [isControlled, onCheckedChange],
    );

    const toggle = React.useCallback(
      (event) => {
        if (disabled) {
          return;
        }
        setChecked(!isChecked, event);
      },
      [disabled, isChecked, setChecked],
    );

    const handleClick = React.useCallback(
      (event) => {
        if (onClick) {
          onClick(event);
        }
        if (!event.defaultPrevented) {
          toggle(event);
        }
      },
      [onClick, toggle],
    );

    const handleKeyDown = React.useCallback(
      (event) => {
        if (onKeyDown) {
          onKeyDown(event);
        }
        if (event.defaultPrevented) {
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggle(event);
        }
      },
      [onKeyDown, toggle],
    );

    return (
      <button
        type="button"
        role="switch"
        aria-checked={isChecked}
        data-state={isChecked ? 'checked' : 'unchecked'}
        aria-disabled={disabled ? 'true' : 'false'}
        ref={ref}
        disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          'tw-relative tw-inline-flex tw-h-[28px] tw-w-[52px] tw-cursor-pointer tw-items-center tw-rounded-full tw-border-2 tw-border-transparent tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-indigo-400/70 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-white disabled:tw-cursor-not-allowed disabled:tw-opacity-50 dark:focus-visible:tw-ring-offset-slate-900',
          isChecked
            ? 'tw-bg-indigo-500 hover:tw-bg-indigo-600 dark:tw-bg-indigo-400 dark:hover:tw-bg-indigo-300'
            : 'tw-bg-slate-200 hover:tw-bg-slate-300 dark:tw-bg-slate-700 dark:hover:tw-bg-slate-600',
          className,
        )}
        {...rest}
      >
        <span
          className={cn(
            'tw-pointer-events-none tw-block tw-h-5 tw-w-5 tw-rounded-full tw-bg-white tw-shadow-lg tw-ring-0 tw-transition-transform dark:tw-bg-slate-900',
            isChecked ? 'tw-translate-x-[24px]' : 'tw-translate-x-0',
          )}
        />
      </button>
    );
  },
);

Switch.displayName = 'Switch';

export { Switch };
